import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { getWrapperRole, type WrapperRole } from '../utils/wrapperRole'
import { safeApiCall, type ApiDegradeResult } from './apiConnectivity'

/**
 * Reads a required Vite env var and throws at module-load time if it is absent.
 * This mirrors the requireEnv pattern in src/services/firebase.ts — fail loudly
 * so a SIT build with a mis-set env never silently targets prod.
 */
const requireEnv = (key: string): string => {
  const value = import.meta.env[key] as string | undefined
  if (!value) {
    throw new Error(
      `Missing ${key}. Add it to .env.sit.local (SIT), .env.dev.local (dev), ` +
      `or the equivalent CI secret. A hardcoded prod fallback was intentionally removed.`,
    )
  }
  return value
}

let initialized = false

export const MEMBER_APK_DOWNLOAD_URL: string = requireEnv('VITE_MEMBER_APK_DOWNLOAD_URL')

export const HELPER_APK_DOWNLOAD_URL: string = requireEnv('VITE_HELPER_APK_DOWNLOAD_URL')

export const resolveMobileUpdateManifestEndpoint = (projectId: string | undefined = import.meta.env.VITE_FIREBASE_PROJECT_ID): string => {
  const explicitUrl = import.meta.env.VITE_MOBILE_UPDATE_MANIFEST_URL
  if (explicitUrl) return explicitUrl
  // Prefer the caller-supplied projectId; if absent, require the env var so a
  // mis-set SIT build fails loud instead of silently targeting prod.
  const resolvedProjectId = projectId || requireEnv('VITE_FIREBASE_PROJECT_ID')
  return `https://asia-south1-${resolvedProjectId}.cloudfunctions.net/mobileUpdateManifest`
}

const MANIFEST_ENDPOINT: string = resolveMobileUpdateManifestEndpoint()

export type UpdateSurface = 'member' | 'helper'

const surfaceForRole = (role: WrapperRole | null): UpdateSurface =>
  role === 'helper' ? 'helper' : 'member'

export const getLatestApkDownloadUrl = (role: WrapperRole | null = getWrapperRole()): string =>
  surfaceForRole(role) === 'helper' ? HELPER_APK_DOWNLOAD_URL : MEMBER_APK_DOWNLOAD_URL

const notifyLocally = async (title: string, body: string) => {
  try {
    if (typeof Notification === 'undefined') return
    const permission = Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission
    if (permission !== 'granted') return
    new Notification(title, { body, tag: 'timelining-mobile-update' })
  } catch {
    // Local notifications are best-effort only; toast still covers in-app notice.
  }
}

export type PendingUpdate = { version: string; bundleId: string | null }

// The bundle id of the most recent 'updateAvailable' event, so callers (a
// settings row, the toast action) can trigger an immediate install without
// re-running getLatest+download.
let lastPendingUpdate: PendingUpdate | null = null

export const getPendingNativeUpdate = (): PendingUpdate | null => lastPendingUpdate

/** Test hook — reset the module-scoped update cache between tests. */
export const __resetNativeUpdateStateForTests = (): void => {
  initialized = false
  lastPendingUpdate = null
}

export const initializeNativeUpdates = (onUpdateAvailable?: (event: PendingUpdate) => void): void => {
  if (initialized || !Capacitor.isNativePlatform()) return
  initialized = true

  void CapacitorUpdater.notifyAppReady().catch((error) => {
    console.warn('[native-update] notifyAppReady failed', error)
  })

  void CapacitorUpdater.addListener('updateAvailable', (event) => {
    const version = event.bundle?.version || event.bundle?.id || 'new version'
    const bundleId = event.bundle?.id || null
    lastPendingUpdate = { version, bundleId }
    onUpdateAvailable?.(lastPendingUpdate)
    void notifyLocally('Timelining update available', `Version ${version} is ready to install.`)
  }).catch((error) => {
    console.warn('[native-update] listener registration failed', error)
  })
}

export const checkNativeUpdateNow = async (): Promise<{ status: 'WEB' | 'CURRENT' | 'AVAILABLE' | 'ERROR'; version?: string; message: string }> => {
  if (!Capacitor.isNativePlatform()) {
    return { status: 'WEB', message: 'Native updates are available only in the Android app.' }
  }
  try {
    const latest = await CapacitorUpdater.getLatest()
    const kind = typeof latest.kind === 'string' ? latest.kind : ''
    const version = typeof latest.version === 'string' ? latest.version : undefined
    if (kind === 'up_to_date' || latest.error === 'no_update_available' || latest.error === 'no_new_version_available') {
      return { status: 'CURRENT', version, message: 'App is already up to date.' }
    }
    if (typeof latest.url === 'string' && latest.url) {
      return { status: 'AVAILABLE', version, message: `Update available${version ? `: ${version}` : ''}. It will download automatically.` }
    }
    return { status: 'CURRENT', version, message: latest.message || 'No update is available.' }
  } catch (error) {
    return { status: 'ERROR', message: error instanceof Error ? error.message : 'Update check failed.' }
  }
}

/**
 * Install the pending update immediately instead of waiting for the app to
 * background or restart. Falls back to a fresh getLatest/download when no
 * `updateAvailable` event has fired yet (e.g. the user opened Settings before
 * autoUpdate had a chance to run).
 */
export const applyNativeUpdateNow = async (
  pending: PendingUpdate | null = lastPendingUpdate,
): Promise<{ status: 'WEB' | 'CURRENT' | 'INSTALLED' | 'ERROR'; version?: string; message: string }> => {
  if (!Capacitor.isNativePlatform()) {
    return { status: 'WEB', message: 'Install-now is available only in the Android app.' }
  }
  try {
    if (pending?.bundleId) {
      await CapacitorUpdater.set({ id: pending.bundleId })
      return { status: 'INSTALLED', version: pending.version, message: 'Installing update…' }
    }
    const latest = await CapacitorUpdater.getLatest()
    const version = typeof latest.version === 'string' ? latest.version : ''
    const url = typeof latest.url === 'string' ? latest.url : ''
    const kind = typeof latest.kind === 'string' ? latest.kind : ''
    if (kind === 'up_to_date' || latest.error === 'no_update_available' || latest.error === 'no_new_version_available' || !url || !version) {
      return { status: 'CURRENT', version, message: 'App is already up to date.' }
    }
    const bundle = await CapacitorUpdater.download({ url, version })
    if (!bundle?.id) {
      return { status: 'ERROR', version, message: 'Download completed but the bundle id is missing.' }
    }
    await CapacitorUpdater.set({ id: bundle.id })
    lastPendingUpdate = { version, bundleId: bundle.id }
    return { status: 'INSTALLED', version, message: 'Installing update…' }
  } catch (error) {
    return { status: 'ERROR', message: error instanceof Error ? error.message : 'Install failed.' }
  }
}

export type PublishedManifest = {
  surface: UpdateSurface
  version: string
  bundleUrl: string
  apkUrl: string | null
  apkChecksum: string | null
  bundleChecksum: string | null
  releasedAt: string | null
  bundleSizeBytes: number | null
  apkSizeBytes: number | null
}

const asOptionalString = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null
const asOptionalNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * Read the published manifest for the given surface directly from the update
 * function. Used by the Devices settings panel to show "Latest published: v…"
 * to signed-in users without opening the native OTA machinery.
 */
export const fetchPublishedManifest = async (
  surface: UpdateSurface,
  fetcher: typeof fetch = typeof fetch === 'function' ? fetch : (undefined as unknown as typeof fetch),
): Promise<PublishedManifest | null> => {
  const result = await fetchPublishedManifestResult(surface, fetcher)
  return result.ok ? result.data : null
}

export const fetchPublishedManifestResult = async (
  surface: UpdateSurface,
  fetcher: typeof fetch = typeof fetch === 'function' ? fetch : (undefined as unknown as typeof fetch),
): Promise<ApiDegradeResult<PublishedManifest | null>> => safeApiCall(async () => {
  if (typeof fetcher !== 'function') return null
  const response = await fetcher(`${MANIFEST_ENDPOINT}?surface=${surface}`, { method: 'GET' })
  if (!response.ok) return null
  const data = (await response.json()) as Record<string, unknown>
  const version = asOptionalString(data.version)
  const bundleUrl = asOptionalString(data.url)
  if (!version || !bundleUrl) return null
  return {
    surface,
    version,
    bundleUrl,
    apkUrl: asOptionalString(data.currentApkUrl) || asOptionalString(data.apkUrl),
    apkChecksum: asOptionalString(data.apkChecksum),
    bundleChecksum: asOptionalString(data.checksum),
    releasedAt: asOptionalString(data.releasedAt),
    bundleSizeBytes: asOptionalNumber(data.bundleSizeBytes),
    apkSizeBytes: asOptionalNumber(data.apkSizeBytes),
  }
})

export const openLatestApkDownload = (): void => {
  const url = getLatestApkDownloadUrl()
  if (typeof window === 'undefined') return
  const opened = window.open(url, '_blank', 'noopener,noreferrer')
  if (!opened) window.location.assign(url)
}

export const copyLatestApkDownloadLink = async (): Promise<void> => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) throw new Error('Clipboard is unavailable.')
  await navigator.clipboard.writeText(getLatestApkDownloadUrl())
}

export const shareLatestApkDownloadLink = async (): Promise<'SHARED' | 'COPIED'> => {
  const url = getLatestApkDownloadUrl()
  if (typeof navigator !== 'undefined' && 'share' in navigator && typeof navigator.share === 'function') {
    await navigator.share({
      title: 'Install Timelining',
      text: 'Install the Timelining Android app from this testing link.',
      url,
    })
    return 'SHARED'
  }
  await copyLatestApkDownloadLink()
  return 'COPIED'
}
